import * as moment from 'moment/moment';
import { ChartBaseModel } from './charts';
import { ReportModel, ReportSettings, ReportType } from './reports';
import  {IPlanData } from "sharedFeatures/sharedModels/IPlanData";
import { BasePlanData } from './BasePlanData';

export class PlanData extends BasePlanData implements IPlanData {

    planStartStr: string = '';
    actualsPlanStartStr: string = '';
    planEndStr: string = '';
    planDurationYears: number = 0;
    planName: string = '';
    planGUID: string = '';
    planYearEnd: number = 0;
    planCurrencyId: number = 0;
    planCurrencyPosition: number = 0;
    planTourActive: boolean = false;
    planWasNotEdited: boolean = true;
    planLogoBlob: string = '';
    planIsNotFound: boolean = false;
    sharedPlanEditMode: string = '';
    ownerLevel: number = 0;
    ownerMaxPlanDuration: number = 0;
    planUrl: string = '';
    xeroConnected: boolean = false;
    crunchConnected: boolean = false;
    planTaxSystem: string = '';
    autoSync: number = 0;
    isQuickUpdate: number = 0;

    lastView: PlanDataLastView = new PlanDataLastView();
    xeroData: XeroPlanDto = new XeroPlanDto();
    crunchImport?: CrunchImportData;
    reportTypes: ReportModel[] = [];
    actualReportTypes: ReportModel[] = [];
    chartTypes: ChartBaseModel[] = [];

    public deserialize(obj: PlanData): void {
        Object.keys(obj).forEach((key) => {
            (this[key as keyof PlanData] as IPlanData) = obj[key as keyof typeof obj];
        });
    }

    get isAutoSyncBool() {
        return this.autoSync === 1;
    }

    get isQuickUpdateBool() {
        return this.isQuickUpdate === 1;
    }

    get ReportSettings(): ReportSettings {
        return {
            DecimalPlaces: parseInt(this.lastView.repDecimalPlaces),
            EmptyAccounts: this.lastView.repEmptyAccounts ? (this.lastView.repEmptyAccounts.toLowerCase() === 'true') : true,
            SummariseFigure: parseInt(this.lastView.repSummariseFigure),
            ActualsEndDate: new Date(this.lastView.repActualsEndDate),
            ShowDifference: this.lastView.repShowDifference,
            ShowVariance: this.lastView.repShowVariance,
            ShowXeroAccounts: this.lastView.repShowXeroAccounts
        };
    }

    get LastReportId(): ReportType {
        const defaultReport = this.reportTypes[0];
        const itemId = this.lastView.repType?.length > 0 ? parseInt(this.lastView?.repType) as ReportType : defaultReport.id;
        const selectedReport = this.reportTypes.find(x => x.id == itemId || x.SubReports?.some(y => y.id == itemId)) ?? defaultReport;
        const selectedSubReportType = selectedReport?.SubReports?.find(x => x.Type == parseInt(this.lastView.repNewReportType));
        return selectedSubReportType?.id ?? selectedReport?.id ?? defaultReport.id;
    }

    isDateWithinDuration(date: string) {
        const checkDate = moment(date, this.dateFormat).toDate().getTime();
        return checkDate >= this.planStartDate.toDate().getTime() && checkDate <= this.planEndDate.toDate().getTime();
    }
}

export interface CrunchImportData {
    Id: number;
    PlanGUID?: string;
    CustomerId: number;
    ImportDate: string;
    Finished: boolean;
    ErrorCode: number;
}

// Xero data
export class XeroPlanDto {
    xeroOrganizationName: string = '';
    xeroLastSyncDate: string = '';
    xeroAccounts: Object = {};
    xeroLinkHeadingFiltersList: string = '';
    isCashflowSyncActive: boolean = false;
    isPLSyncActive: boolean = false;
    isNeedUpdatePlan: boolean = false;
    processedTransactionsCount: number = 0;
    currentMonthCounter: number = 0;
}

// Stored last view of plan
export class PlanDataLastView {
    route: string = ''; // content.id

    section: string = ''; // selected section

    dbSection: string = ''; // dashboardSectionId
    dbGroup: string = ''; // dashboardGroupId
    dbDuration: string = ''; // dashboardDurationYear
    dbRange: string = ''; // dashboardDateRange
    dbCustomDates: string = ''; // dashboardUseCustomDates
    dbStart: string = ''; // dashboardStartDate
    dbEnd: string = ''; // dashboardEndDate
    dbDateFormat: string = ''; // dashboardCustomDateFormat

    repType: string = ''; // reportsDataType
    repShowActuals: string = ''; // reportsShowActuals
    repActualsLayout: string = ''; // reportsActualsLayout
    repDuration: string = ''; // reportsDurationYear
    repRange: string = ''; // reportsDateRange
    repUseCurrency: string = ''; // reportsUseCurrencySymbol
    repCustomDates: string = ''; // reportsUseCustomDates
    repStart: string = ''; // reportsStartDate
    repEnd: string = ''; // reportsEndDate
    repDateFormat: string = ''; // reportsCustomDateFormat
    repEmptyAccounts: string = ''; //reportsEmptyAccounts
    repDecimalPlaces: string = ''; //reportsDecimalPlaces
    repSummariseFigure: string = ''; //reportsSummariseFigure
    repNewReportType: string = ''; //reportsNewReportType
    repActualsEndDate: string = ''; //reportsActualsEndDate

    acType: string = ''; // actualsType
    acDuration: string = ''; // actualsDurationYear

    dlDuration: string = ''; // downloadDurationYear
    dlRange: string = ''; // downloadDateRange
    dlCustomDates: string = ''; // downloadUseCustomDates
    dlStart: string = ''; // downloadStartDate
    dlEnd: string = ''; // downloadEndDate
    dlDateFormat: string = ''; // downloadCustomDateFormat

    repShowDifference?: boolean;
    repShowVariance?: boolean;
    repShowXeroAccounts?: boolean;

    dashboardItems?: string;

    isGeneralSettingsCollapsed?: boolean;
    isChildAssetsCollapsed?: boolean;
    isXeroMatchCollapsed?: boolean;
    isInflationCollapsed?: boolean;
    isInflationAdjustmentsCollapsed?: boolean;
    isRentalAdjustmentRateCollapsed?: boolean;
    isSoniaCollapsed?: boolean;
    isSavingsCollapsed?: boolean;
    isInsuranceCollapsed?: boolean;
    isTimingCollapsed?: boolean;
    isCoprTaxCollapsed?: boolean;
    isCoprTaxOBCollapsed?: boolean;
    isTaxCollapsed?: boolean;
    isTaxPaymentsCollapsed?: boolean;
    isTaxOBCollapsed?: boolean;
}
